import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Divider,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGetBankAccountsQuery,
  useGetUnclearedTransactionsQuery,
  useGetBankReconciliationQuery,
  useSaveBankReconciliationDraftMutation,
  useCompleteBankReconciliationMutation,
} from '../../../store/api/financeApi';

function BankReconciliationForm() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const [bankAccountId, setBankAccountId] = useState('');
  const [statementDate, setStatementDate] = useState(new Date().toISOString().split('T')[0]);
  const [openingBalance, setOpeningBalance] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const [notes, setNotes] = useState('');
  
  const [selectedLines, setSelectedLines] = useState(new Set());

  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const { data: existingRec } = useGetBankReconciliationQuery(id, { skip: isNew });

  // Only fetch uncleared if we have account and date, and it's either new OR we are editing a draft
  const fetchUncleared = !!bankAccountId && !!statementDate && (isNew || (existingRec && existingRec.status === 'draft'));
  
  const { data: unclearedLines = [] } = useGetUnclearedTransactionsQuery(
    { bankAccountId, statementDate },
    { skip: !fetchUncleared }
  );

  const [saveDraft, { isLoading: isSaving }] = useSaveBankReconciliationDraftMutation();
  const [completeRec, { isLoading: isCompleting }] = useCompleteBankReconciliationMutation();

  useEffect(() => {
    if (!isNew && existingRec) {
      setBankAccountId(existingRec.bankAccountId);
      setStatementDate(existingRec.statementDate.split('T')[0]);
      setOpeningBalance(existingRec.openingBalance);
      setClosingBalance(existingRec.closingBalance);
      setNotes(existingRec.notes || '');

      // Pre-select the lines that were already selected in this draft/completed rec
      const preSelected = new Set();
      existingRec.journalLines?.forEach(line => preSelected.add(line.id));
      setSelectedLines(preSelected);
    }
  }, [isNew, existingRec]);

  // Combine uncleared lines with already selected lines from the draft
  const displayLines = useMemo(() => {
    if (!isNew && existingRec && existingRec.status === 'completed') {
      return existingRec.journalLines || [];
    }
    
    // For new/draft, combine uncleared and existing selected
    const linesMap = new Map();
    unclearedLines.forEach(l => linesMap.set(l.id, l));
    if (existingRec?.journalLines) {
      existingRec.journalLines.forEach(l => linesMap.set(l.id, l));
    }
    
    // Sort by date
    return Array.from(linesMap.values()).sort((a, b) => new Date(a.journalEntry.entryDate) - new Date(b.journalEntry.entryDate));
  }, [isNew, existingRec, unclearedLines]);

  const handleToggleLine = (lineId) => {
    const newSelected = new Set(selectedLines);
    if (newSelected.has(lineId)) {
      newSelected.delete(lineId);
    } else {
      newSelected.add(lineId);
    }
    setSelectedLines(newSelected);
  };

  const totals = useMemo(() => {
    let clearedDeposits = 0;
    let clearedWithdrawals = 0;

    displayLines.forEach(line => {
      if (selectedLines.has(line.id) || (existingRec?.status === 'completed')) {
        clearedDeposits += Number(line.debit || 0);
        clearedWithdrawals += Number(line.credit || 0);
      }
    });

    const ob = Number(openingBalance) || 0;
    const cb = Number(closingBalance) || 0;
    const clearedBal = ob + clearedDeposits - clearedWithdrawals;
    const diff = cb - clearedBal;

    return { clearedDeposits, clearedWithdrawals, clearedBal, diff };
  }, [displayLines, selectedLines, openingBalance, closingBalance, existingRec]);

  const handleSave = async (isComplete = false) => {
    if (!bankAccountId) return alert('Select a bank account');
    if (!statementDate) return alert('Select statement date');

    const payload = {
      id: isNew ? undefined : id,
      bankAccountId,
      statementDate,
      openingBalance: Number(openingBalance || 0),
      closingBalance: Number(closingBalance || 0),
      notes,
      selectedLineIds: Array.from(selectedLines)
    };

    try {
      if (isComplete) {
        if (Math.abs(totals.diff) > 0.01) {
          return alert('Difference must be 0 to complete reconciliation.');
        }
        await completeRec(payload).unwrap();
        navigate('/finance/bank-reconciliation');
      } else {
        const res = await saveDraft(payload).unwrap();
        if (isNew) {
          navigate(`/finance/bank-reconciliation/${res.id}`);
        }
      }
    } catch (err) {
      alert(err?.data?.message || 'Failed to save');
    }
  };

  const isReadOnly = existingRec?.status === 'completed';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/finance/bank-reconciliation')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {isNew ? 'New Bank Reconciliation' : isReadOnly ? 'Bank Reconciliation (Completed)' : 'Edit Draft Reconciliation'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isReadOnly && (
            <>
              <Button variant="outlined" onClick={() => handleSave(false)} disabled={isSaving || isCompleting}>
                Save Draft
              </Button>
              <Button variant="contained" color="success" onClick={() => handleSave(true)} disabled={isSaving || isCompleting || Math.abs(totals.diff) > 0.01}>
                Complete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{xs:12,md:4}}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Statement Info</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Bank Account</InputLabel>
                  <Select
                    value={bankAccountId}
                    label="Bank Account"
                    onChange={(e) => setBankAccountId(e.target.value)}
                    disabled={!isNew}
                  >
                    {bankAccounts.map((acc) => (
                      <MenuItem key={acc.id} value={acc.id}>
                        {acc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Statement Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={statementDate}
                  onChange={(e) => setStatementDate(e.target.value)}
                  disabled={!isNew && isReadOnly}
                />

                <TextField
                  label="Opening Balance"
                  type="number"
                  fullWidth
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  disabled={!isNew && isReadOnly}
                />

                <TextField
                  label="Closing Balance"
                  type="number"
                  fullWidth
                  value={closingBalance}
                  onChange={(e) => setClosingBalance(e.target.value)}
                  disabled={!isNew && isReadOnly}
                />

                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={!isNew && isReadOnly}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs:12,md:8}}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent>
              <Grid container spacing={2} sx={{ mb: 2, textAlign: 'center' }}>
                <Grid size={{xs:3}}>
                  <Typography variant="body2" color="text.secondary">Cleared Deposits</Typography>
                  <Typography variant="h6" color="success.main">+{totals.clearedDeposits.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{xs:3}}>
                  <Typography variant="body2" color="text.secondary">Cleared Withdrawals</Typography>
                  <Typography variant="h6" color="error.main">-{totals.clearedWithdrawals.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{xs:3}}>
                  <Typography variant="body2" color="text.secondary">Cleared Balance</Typography>
                  <Typography variant="h6">{totals.clearedBal.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{xs:3}}>
                  <Typography variant="body2" color="text.secondary">Difference</Typography>
                  <Typography variant="h6" color={Math.abs(totals.diff) < 0.01 ? 'success.main' : 'error.main'}>
                    {totals.diff.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }} />

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {!isReadOnly && <TableCell padding="checkbox" />}
                      <TableCell>Date</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Payee/Memo</TableCell>
                      <TableCell align="right">Deposit (Dr)</TableCell>
                      <TableCell align="right">Withdrawal (Cr)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayLines.map(line => {
                      const isSelected = selectedLines.has(line.id) || isReadOnly;
                      return (
                        <TableRow key={line.id} hover onClick={() => !isReadOnly && handleToggleLine(line.id)} sx={{ cursor: isReadOnly ? 'default' : 'pointer' }}>
                          {!isReadOnly && (
                            <TableCell padding="checkbox">
                              <Checkbox checked={isSelected} />
                            </TableCell>
                          )}
                          <TableCell>{new Date(line.journalEntry.entryDate).toLocaleDateString()}</TableCell>
                          <TableCell>{line.journalEntry.referenceType} {line.journalEntry.referenceId?.substring(0,8)}</TableCell>
                          <TableCell>{line.journalEntry.payee || line.journalEntry.memo}</TableCell>
                          <TableCell align="right">{Number(line.debit) > 0 ? Number(line.debit).toFixed(2) : '-'}</TableCell>
                          <TableCell align="right">{Number(line.credit) > 0 ? Number(line.credit).toFixed(2) : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                    {displayLines.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No transactions found to reconcile.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BankReconciliationForm;
